import React, { useEffect, useState, useRef } from 'react';
import constants from '../../util/constants';
import { getLastMove, getMove, getPrevMoveNotation, getCurrentVariation, parseMove, resetPositionCache } from '../../util/Chess';
import clsx from 'clsx';
import './Moves.css';

const CHESSBOARD = constants.CHESSBOARD;

const Moves = ({
  moves,
  starredMoves,
  currentMove,
  setCurrentMove,
  type,
  userId,
  currentTurn,
  className,
  handleVariationNavigation,
  gameResultInfo,
  commentPreviewMoves,
  setCommentPreviewMoves,
  isAnnotationPreview = false,
  startingTurn = 'w'
}) => {
  // Add a deep scanning function to find comment moves in nested variations
  const findCommentMovesInVariations = (movesArray) => {
    const result = [];

    if (!movesArray || !movesArray.length) return result;

    // Helper function to recursively scan through variations
    const scanForCommentMoves = (moves, path = "") => {
      if (!moves || !moves.length) return;

      moves.forEach((move, moveIndex) => {
        const currentPath = path ? `${path}${moveIndex}` : `${moveIndex}`;

        // Check if this move is a comment move
        if (move?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT) {
          result.push({
            path: currentPath,
            commentId: move.creator.id,
            notation: move.notation
          });
        }

        // Check variations recursively
        if (move.variations && move.variations.length) {
          move.variations.forEach((variation, varIndex) => {
            scanForCommentMoves(variation, `${currentPath}|${varIndex}:`);
          });
        }
      });
    };

    scanForCommentMoves(movesArray);
    return result;
  };



  // Track the current trio index
  const [currentTrioIndex, setCurrentTrioIndex] = useState(0);
  // Reference to the moves container
  const movesContainerRef = useRef(null);
  // Simplified animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState(null);

  // Add state for tracking comment preview navigation
  const [isInCommentPreview, setIsInCommentPreview] = useState(false);

  // Track when user navigates away from preview moves
  useEffect(() => {
    // Only run this effect if we were previously in a comment preview AND this is NOT the annotation preview instance itself
    if (isInCommentPreview && !isAnnotationPreview) {
      let isStillInPreviewedContext = false;

      // Check if a preview is active
      if (commentPreviewMoves.commentId !== null && commentPreviewMoves.parentMove !== null && commentPreviewMoves.variationMove !== null) {
        try {
          // Check 1: Is the current move the original parent move?
          if (currentMove === commentPreviewMoves.parentMove) {
            isStillInPreviewedContext = true;
          }

          // Check 2: Does the current move belong to the previewed variation branch?
          if (!isStillInPreviewedContext) {
            // Get the prefix of the first move in the previewed variation
            const variationMoveFull = commentPreviewMoves.variationMove;
            const variationPrefixMatch = variationMoveFull.match(/^(.*\|\d+:)/);
            const variationBranchPrefix = variationPrefixMatch ? variationPrefixMatch[1] : null;

            // If we have a branch prefix and the current move starts with it, we're still in the context
            if (variationBranchPrefix && currentMove.startsWith(variationBranchPrefix)) {
              isStillInPreviewedContext = true;
            }
          }
        } catch (e) {
          console.error("Error parsing move prefixes for comment preview check", e);
          // If parsing fails, default to potentially resetting to avoid getting stuck
          isStillInPreviewedContext = false;
        }
      }

      // If the current move is NO LONGER considered part of the previewed context...
      if (!isStillInPreviewedContext) {

        // Reset the preview state and the flag
        setIsInCommentPreview(false);
        setCommentPreviewMoves({
          parentMove: null,
          variationMove: null,
          commentId: null
        });
      }
    }
  }, [currentMove, isInCommentPreview, commentPreviewMoves, setCommentPreviewMoves, isAnnotationPreview]);

  // Add CSS for comment preview - moved to top level
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id = 'comment-preview-styles';
    styleEl.textContent = `
      .comment-preview-container {
        display: flex;
        align-items: center;

        border-radius: 8px;
        margin-right: 8px;
        border-left: 3px solid #3066be;
        border-right: 3px solid #3066be;
      }
      
      .comment-preview-moves {
        display: flex;
        align-items: center;
      }
      
      .comment-preview-parent-move, 
      .comment-preview-variation-move {
        background: transparent;
        border: none;
        font-size: 14px;
        cursor: pointer;
        border-radius: 3px;
        font-weight: bold;
      }
      
      .comment-preview-parent-move {
        color: gold;
        font-size: 18px;
      }
      
      .comment-preview-variation-move {
        color: #ff6699;
        font-size: 18px;

      }
      
      .comment-preview-parent-move:hover, 
      .comment-preview-variation-move:hover {
        background: rgba(255, 255, 255, 0.2);
      }
      
      .comment-preview-arrow {
        color: gold;
        margin: 0 2px;
        font-size: 16px;
        font-weight: bold;
      }
      
      .comment-preview-more {
        color: #ff6699;
        margin-left: 2px;
        font-size: 12px;
        font-weight: bold;
      }
    `;

    document.head.appendChild(styleEl);

    return () => {
      if (document.getElementById('comment-preview-styles')) {
        document.getElementById('comment-preview-styles').remove();
      }
    };
  }, []);

  // Update current trio index when current move changes
  useEffect(() => {
    if (currentMove === '-1' || !moves?.length) {
      setCurrentTrioIndex(0);
      return;
    }

    // Calculate which trio contains the current move
    const moveIndex = parseInt(currentMove.split('|')[0].split(':')[0]);
    const newTrioIndex = Math.floor(moveIndex / 2);

    // Don't animate if it's the same trio
    if (newTrioIndex === currentTrioIndex) {
      return;
    }

    // Set new trio index immediately - no delay
    setCurrentTrioIndex(newTrioIndex);

    // Determine animation direction for surrounding elements
    const direction = newTrioIndex > currentTrioIndex ? 'left' : 'right';

    // Set animation flag and direction
    setIsAnimating(true);
    setAnimationDirection(direction);

    // Short timeout to remove animation classes
    setTimeout(() => {
      setIsAnimating(false);
      setAnimationDirection(null);
    }, 100); // Match the transition duration

  }, [currentMove, moves, currentTrioIndex]);

  // Original centering effect for smooth scrolling
  // useEffect(() => {
  //   // Only apply centering if we're not using the trio grid view
  //   if (type === 'COMMENTS') {
  //     const centerCurrentMove = () => {
  //       const container = document.querySelector('.moves-container');

  //       if (!container) return;

  //       // If we're at the reset state (-1), scroll to the beginning
  //       if (currentMove === '-1') {
  //         container.scrollTo({
  //           left: 0,
  //           behavior: 'smooth'
  //         });
  //         return;
  //       }

  //       const currentMoveElement = document.querySelector('.current-move');

  //       if (!currentMoveElement) return;

  //       // Get all necessary measurements
  //       const containerWidth = container.offsetWidth;
  //       const currentMoveWidth = currentMoveElement.offsetWidth;
  //       const currentMoveLeft = currentMoveElement.offsetLeft;
  //       const totalContentWidth = container.scrollWidth;

  //       // Clear any existing margins and only add to last move
  //       const moves = container.querySelectorAll('.move');
  //       moves.forEach(move => {
  //         move.style.marginRight = '0px';  // Reset all margins
  //       });

  //       const lastMove = moves[moves.length - 1];
  //       if (lastMove) {
  //         const spaceNeeded = Math.max(0, (containerWidth - currentMoveWidth) / 2 - 20);
  //         lastMove.style.marginRight = `${spaceNeeded}px`;
  //       }

  //       // Calculate the target scroll position to center the current move
  //       const targetScrollPosition = Math.max(
  //         0, // Don't scroll past the start
  //         Math.min(
  //           currentMoveLeft - (containerWidth - currentMoveWidth) / 2,
  //           totalContentWidth - containerWidth // Don't scroll past the end
  //         )
  //       );

  //       // Apply the scroll with smooth animation
  //       container.scrollTo({
  //         left: targetScrollPosition,
  //         behavior: 'smooth'
  //       });
  //     };

  //     // Initial centering
  //     centerCurrentMove();

  //     // Add resize listener to handle window/container size changes
  //     const resizeObserver = new ResizeObserver(() => {
  //       centerCurrentMove();
  //     });

  //     const container = document.querySelector('.moves-container');
  //     if (container) {
  //       resizeObserver.observe(container);
  //     }

  //     // Cleanup
  //     return () => {
  //       if (container) {
  //         resizeObserver.disconnect();
  //       }
  //     };
  //   }
  // }, [currentMove, type]); // Re-run when currentMove changes

  const onReset = () => {
    setCurrentMove('-1');
  };

  const onEnd = () => {
    const lastMove = getLastMove(moves);
    setCurrentMove(`${lastMove}`);
  };

  let mainMoves = moves; // Commented moves
  const renderCommentMoves = (moves, prefix, commentId, numMoves) => {
    const firstCommentMove = moves.findIndex(
      (move) =>
        move?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
        move?.creator?.id === commentId,
    );

    // For TYPE=COMMENTS context, use the trio style rendering to match variations
    if (type === 'COMMENTS') {
      return renderCommentTrioMoves(moves, prefix, commentId, numMoves, null);
    }

    // Otherwise, use the original inline rendering for comments embedded in normal game views
    const movesJsx = moves?.map((move, index) => {
      return (
        <>
          {move?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
            move.creator.id === commentId && (
              <>
                {index === firstCommentMove && (
                  <button
                    onClick={() =>
                      handleMoveClick(
                        getPrevMoveNotation(`${prefix || ''}${index}`),
                      )
                    }
                    className="back-from-variation-btn"
                  >
                    {
                      getMove(
                        mainMoves,
                        getPrevMoveNotation(`${prefix || ''}${index}`),
                      ).notation
                    }
                  </button>
                )}
                {index == 0 && Number.isInteger(numMoves) && (
                  <span className="move-spacer move-number">
                    {Math.ceil(numMoves) + Math.ceil(index / 2 + 1)}.
                  </span>
                )}
                {index % 2 == Number.isInteger(numMoves) && (
                  <span className="move-spacer move-number">
                    {Math.ceil(numMoves) + Math.ceil(index / 2 + 1)}.
                  </span>
                )}
                {Number.isInteger(numMoves) && index === 0 ? (
                  <span className="move-spacer">..</span>
                ) : (
                  <></>
                )}
                <button
                  key={index}
                  onClick={() => handleMoveClick(`${prefix || ''}${index}`)}
                  id={`${prefix || ''}${index}`}
                  className={`comment-move${`${prefix || ''}${index}` === currentMove
                    ? ' current-comment-move' // Current move for comments
                    : ''
                    }`}
                  style={{ display: 'inline-flex' }}
                >
                  {move.notation}
                </button>
              </>
            )}
        </>
      );
    });
    const variationJsx = moves?.map((move, index) => {
      const currentMoveNotation = `${prefix || ''}${index}`;
      return (
        <>
          {move.variations &&
            move.variations.length !== 0 &&
            move.variations.map((variation, variationIndex) => {
              if (!variation) {
                return (
                  <React.Fragment
                    key={`${currentMoveNotation}-${variationIndex}`}
                  />
                );
              }
              if (variation.length === 0) {
                return (
                  <React.Fragment
                    key={`${currentMoveNotation}-${variationIndex}`}
                  />
                );
              }

              return (
                <div
                  className="variation"
                  key={`${currentMoveNotation}-${variationIndex}`}
                >
                  {renderCommentMoves(
                    variation,
                    `${prefix || ''}${index}|${variationIndex}:`,
                    commentId,
                    Math.ceil(numMoves) + index / 2,
                  )}
                </div>
              );
            })}
        </>
      );
    });

    return (
      <>
        {movesJsx && (
          <div // MOVE CONTAINER TRIPLE
            // className={`variation-moves-container`} Causes issues
            style={{
              fontSize: '20px'
            }}
          >
            {movesJsx}
          </div>
        )}
        {variationJsx}
        {/* REAL VARIATION CONTAINER FOR COMMENTS */}
      </>
    );
  };

  // New function for rendering comments in the trio style, similar to variations
  const renderCommentTrioMoves = (moves, prefix, commentId, numMoves, variationStartingTurn) => {
    if (!moves || moves.length === 0) {
      return null;
    }

    // Filter moves for this comment ID
    const filteredMoves = moves.filter(
      move => move?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT && 
      move?.creator?.id === commentId
    );

    if (filteredMoves.length === 0) {
      return null;
    }

    // Group moves into trios
    const trios = [];
    let currentTrio = { moveNumber: null, whiteMove: null, blackMove: null, moves: [] };

    // Determine if we're starting with Black's move
    // Use the variation-specific startingTurn if provided, otherwise fall back to the global startingTurn
    const isStartingWithBlack = (variationStartingTurn || startingTurn) === 'b';

    filteredMoves.forEach((move, index) => {
      const moveIndex = index;
      // Adjust the move parity based on starting turn
      const isWhiteMove = isStartingWithBlack ? 
        (moveIndex % 2 !== 0) : // If starting with black, odd indices are white moves
        (moveIndex % 2 === 0);  // If starting with white, even indices are white moves

      // Calculate appropriate move number
      let moveNumber;
      if (isStartingWithBlack) {
        moveNumber = Math.floor(moveIndex / 2) + 1;
        // For White's moves after Black started, increment the move number by 1
        if (isWhiteMove) {
          moveNumber += 1;
        }
      } else {
        moveNumber = Math.floor(moveIndex / 2) + 1;
      }

      if (isWhiteMove) {
        // Start a new trio for white's move
        currentTrio = {
          moveNumber: moveNumber,
          whiteMove: move,
          blackMove: null,
          whiteIndex: moveIndex,
          blackIndex: null,
          moves: [move]
        };
        trios.push(currentTrio);
      } else {
        // Add black's move to the current trio
        if (trios.length > 0) {
          const lastTrio = trios[trios.length - 1];
          lastTrio.blackMove = move;
          lastTrio.blackIndex = moveIndex;
          lastTrio.moves.push(move);
        } else {
          // Handle case where comment starts with black
          currentTrio = {
            moveNumber: moveNumber,
            whiteMove: null,
            blackMove: move,
            whiteIndex: null,
            blackIndex: moveIndex,
            moves: [move]
          };
          trios.push(currentTrio);
        }
      }
    });

    // Determine which trio is current based on the currentMove
    let trioIndex = 0;
    let currentMoveIndex = -1;

    if (currentMove !== '-1' && currentMove) {
      try {
        // Parse the current move notation to get the index
        if (typeof currentMove === 'string' && currentMove.includes('|')) {
          const parts = currentMove.split('|');
          if (parts.length > 1 && parts[1]) {
            if (parts[1].includes(':')) {
              const variationParts = parts[1].split(':');
              if (variationParts.length > 1) {
                currentMoveIndex = parseInt(variationParts[variationParts.length - 1]);
                
                // Find which trio contains the current move
                trios.forEach((trio, index) => {
                  if ((trio.whiteIndex === currentMoveIndex) || (trio.blackIndex === currentMoveIndex)) {
                    trioIndex = index;
                  }
                });
              }
            } else {
              // Handle the case where there's no colon
              currentMoveIndex = parseInt(parts[1]);
              
              // Find which trio contains the current move
              trios.forEach((trio, index) => {
                if ((trio.whiteIndex === currentMoveIndex) || (trio.blackIndex === currentMoveIndex)) {
                  trioIndex = index;
                }
              });
            }
          }
        } else {
          // Handle regular move without variation notation (no pipe character)
          currentMoveIndex = parseInt(currentMove);
          
          // Find which trio contains the current move
          trios.forEach((trio, index) => {
            if ((trio.whiteIndex === currentMoveIndex) || (trio.blackIndex === currentMoveIndex)) {
              trioIndex = index;
            }
          });
        }
      } catch (error) {
        console.error("Error parsing current move in renderMoves:", error, currentMove);
        // Default values already set above
      }
    }

    

    // Calculate displayIndices - which trios to show
    let displayIndices = [];
    

    if (!isAnimating) {
      // Normal display - previous, current, next
      const prevIndex = trioIndex > 0 ? trioIndex - 1 : null;
      const nextIndex = trioIndex < trios.length - 1 ? trioIndex + 1 : null;
      displayIndices = [prevIndex, trioIndex, nextIndex].filter(i => i !== null);
    } else {
      // During animation - show 4 trios to enable the slide animation
      if (animationDirection === 'left') {
        // Sliding left - show current, next, and next+1
        const nextIndex = trioIndex < trios.length - 1 ? trioIndex + 1 : null;
        const nextNextIndex = nextIndex !== null && nextIndex < trios.length - 1 ? nextIndex + 1 : null;
        displayIndices = [trioIndex, nextIndex, nextNextIndex].filter(i => i !== null);
      } else {
        // Sliding right - show prev-1, prev, and current
        const prevIndex = trioIndex > 0 ? trioIndex - 1 : null;
        const prevPrevIndex = prevIndex !== null && prevIndex > 0 ? prevIndex - 1 : null;
        displayIndices = [prevPrevIndex, prevIndex, trioIndex].filter(i => i !== null);
      }
    }

    // Determine if we're at the beginning or end of the game
    const isAtStart = trioIndex === 0;
    const isAtEnd = trioIndex === trios.length - 1;

    // Determine the end text based on game result
    let endText = "End";
    let endTextColor = "#999";
    if (gameResultInfo && isAtEnd) {
      if (gameResultInfo.result === 'draw') {
        endText = "Draw";
        endTextColor = "tan"; // Blue for draw
      } else if (gameResultInfo.result === 'checkmate') {
        endText = "Check-\nMate";
        endTextColor = "#b71c1c"; // Red for checkmate
      }
    }
    // --- END FIX ---

    // Render the grid with proper animation classes
    return (
      <div className="comment-with-back-btn">
        <div className="comment-moves-container">
          {/* Current trio */}
          <div className="trio-container comment-trio-current">
            {renderTrio(trios[trioIndex], prefix, true, trioIndex === 0, variationStartingTurn)}
          </div>

          {/* Next trio or placeholder */}
          {trioIndex < trios.length - 1 ? (
            <div className="trio-container comment-trio-next">
              {renderTrio(trios[trioIndex + 1], prefix, false, false, variationStartingTurn)}
            </div>
          ) : (
            <div className="trio-placeholder-text">...build your line...</div>
          )}

          {/* Empty div as placeholder if no next trio, to maintain grid structure */}
          {/* {nextTrioIndices.length === 0 && <div></div>} */}

          {/* Display empty indicator at end - REMOVED */}
          {/* {isAtEnd && (
            <div
              className="comment-trio-empty-next"
              onClick={onEnd}
            >
              End
            </div>
          )} */}
        </div>
      </div>
    );
  };

  // Render nested comment variations in trio style
  const commentVariationTrioJsx = (moves, prefix, commentId, numMoves) => {
    return moves?.map((move, index) => {
      const currentMoveNotation = `${prefix || ''}${index}`;
      return (
        <>
          {move.variations &&
            move.variations.length !== 0 &&
            move.variations.map((variation, variationIndex) => {
              if (!variation || variation.length === 0) {
                return (
                  <React.Fragment key={`${currentMoveNotation}-${variationIndex}`} />
                );
              }

              // Check if we're in this variation
              const isInVariation = currentMove.startsWith(`${currentMoveNotation}|${variationIndex}:`);

              if (isInVariation) {
                // Determine the starting color for this specific variation
                // We need to know if the parent move is white or black to determine
                // which color moves first in the variation
                const isParentMoveWhite = (index % 2 === 0) === (startingTurn === 'w');
                
                // The first move in a variation is the opposite color of the parent move
                const variationStartingTurn = isParentMoveWhite ? 'b' : 'w';

                return (
                  <div className="comment-variation-box" key={`${currentMoveNotation}-${variationIndex}`}>
                    {/* Use the trio style for nested comment variations */}
                    {renderCommentTrioMoves(
                      variation,
                      `${prefix || ''}${index}|${variationIndex}:`,
                      commentId,
                      Math.ceil(numMoves) + index / 2,
                      variationStartingTurn
                    )}
                  </div>
                );
              } else {
                return null; // Only show the variation if we're in it
              }
            })}
        </>
      );
    });
  };

  // Handler for move clicks with variation checking
  const handleMoveClick = (moveNotation) => {
    console.log('[MOVES-FEN] handleMoveClick called:', {
      moveNotation,
      currentMove,
      isVariation: moveNotation.includes('|')
    });
    
    // If we're clicking the move we're already on, do nothing
    if (moveNotation === currentMove) {
      
      return;
    }

    // If handleVariationNavigation is provided and returns false,
    // it means we need to show a confirmation dialog
    if (handleVariationNavigation && !handleVariationNavigation(moveNotation)) {
      
      return; // Don't navigate yet, waiting for confirmation
    }

    // Check if we're navigating to a variation move
    const isVariationMove = moveNotation.includes('|');
    console.log('[MOVES-FEN] Will navigate to move:', {
      moveNotation, 
      isVariation: isVariationMove
    });
    
    // Clear any previous transitions by forcing a sync update
    // This prevents react batching from causing visual glitches
    if (isVariationMove) {
      
      
      // Reset the position cache to ensure fresh calculation
      resetPositionCache();
      
      // For variation moves, do a direct update to avoid Flash of Incorrect Position
      // This ensures we go straight to the variation position without showing main line first
      try {
        // Set a flag in window to tell Board.jsx this is a direct variation move
        window.chessDirectVariationMove = {
          moveNotation: moveNotation,
          timestamp: Date.now()
        };
      } catch (e) {
        console.error('[MOVES-FEN] Error setting window flag', e);
      }
    }
    
    // Set the current move
    
    setCurrentMove(moveNotation);
  };

  // New function to render a compact preview of comment variations
  const renderCommentPreview = (moves, commentId) => {


    if (!moves || moves.length === 0) {

      return null;
    }

    // Find variations containing this comment ID
    let variationsWithCommentId = [];
    moves.forEach((move, index) => {
      if (move.variations) {
        move.variations.forEach((variation, varIndex) => {
          const hasThisCommentId = variation.some(
            varMove => varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
              varMove?.creator?.id === commentId
          );

          if (hasThisCommentId) {
            variationsWithCommentId.push({
              parentMove: move,
              parentIndex: index,
              variationIndex: varIndex,
              variation: variation,
              firstCommentMove: variation.find(
                varMove => varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT &&
                  varMove?.creator?.id === commentId
              )
            });
          }
        });
      }
    });



    if (variationsWithCommentId.length === 0) {

      return null;
    }

    // For preview, just show the first variation we found
    const variation = variationsWithCommentId[0];
    const parentMoveNotation = `${variation.parentIndex}`;
    const variationNotation = `${variation.parentIndex}|${variation.variationIndex}:0`;

    // Find the first comment move
    const firstCommentMove = variation.firstCommentMove || variation.variation[0];

    // Get the move number for display (handle both 0-based and 1-based)
    const moveNum = variation.parentMove.moveNum || Math.floor(variation.parentIndex / 2) + 1;

    // Custom click handlers for comment preview moves
    const handleCommentPreviewParentMoveClick = () => {
      // Set state to track that we're in a preview and store the relevant moves
      setIsInCommentPreview(true);
      setCommentPreviewMoves({
        parentMove: parentMoveNotation,
        variationMove: variationNotation,
        commentId: commentId
      });


      // Navigate to the parent move
      handleMoveClick(parentMoveNotation);
    };

    const handleCommentPreviewVariationMoveClick = () => {
      // Set state to track that we're in a preview and store the relevant moves
      setIsInCommentPreview(true);
      setCommentPreviewMoves({
        parentMove: parentMoveNotation,
        variationMove: variationNotation,
        commentId: commentId
      });


      // Navigate to the variation move
      handleMoveClick(variationNotation);
    };

    return (
      <div className="comment-preview-container">
        <div className="comment-preview-moves">
          <button
            className="comment-preview-parent-move"
            onClick={handleCommentPreviewParentMoveClick}
            title="View main line move"
          >
            {moveNum}. {variation.parentMove.notation}
          </button>

          {/* <span className="comment-preview-arrow">→</span> */}
          <div s>
            <i class="fa-solid fa-arrow-right" style={{ color: 'gold' }}></i>
          </div>
          <button
            className="comment-preview-variation-move"
            onClick={handleCommentPreviewVariationMoveClick}
            title="View variation"
          >
            {firstCommentMove.notation}
          </button>

          {variation.variation.length > 1 && (
            <span className="comment-preview-more" title="More moves in this variation">...</span>
          )}
        </div>
      </div>
    );
  };

  // Function to check if we're currently in a variation
  const isInVariation = (moveNotation) => {
    return typeof moveNotation === 'string' && moveNotation.includes('|');
  };

  // Function to get the main line move from a variation move
  const getMainLineMoveFromVariation = (moveNotation) => {
    if (!isInVariation(moveNotation)) return moveNotation;
    return moveNotation.split('|')[0];
  };

  // Modified renderMoves function to group moves into trios
  const renderMoves = (moves, prefix, numMoves) => {
    if (!moves?.length) return null;

    // Group moves into trios (move number + white move + black move)
    const trios = [];
    let currentTrio = { moveNumber: null, whiteMove: null, blackMove: null, moves: [] };

    // Determine if we're starting with Black's move
    const isStartingWithBlack = startingTurn === 'b';

    moves.forEach((move, index) => {
      // Skip comment moves
      if (move?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT) {
        return;
      }

      const moveIndex = index;
      // Adjust the move parity based on starting turn
      const isWhiteMove = isStartingWithBlack ? 
        (moveIndex % 2 !== 0) : // If starting with black, odd indices are white moves
        (moveIndex % 2 === 0);  // If starting with white, even indices are white moves

    

      // Calculate appropriate move number
      // If starting with Black, White's moves should increment the move number
      let moveNumber;
      if (isStartingWithBlack) {
        moveNumber = Math.floor(moveIndex / 2) + 1;
        // For White's moves after Black started, increment the move number by 1
        if (isWhiteMove) {
          moveNumber += 1;
        }
      } else {
        moveNumber = Math.floor(moveIndex / 2) + 1;
      }

      if (isWhiteMove) {
        // Start a new trio for white's move
        currentTrio = {
          moveNumber: moveNumber,
          whiteMove: move,
          blackMove: null,
          whiteIndex: moveIndex,
          blackIndex: null,
          moves: [move]
        };
        
        trios.push(currentTrio);
      } else {
        // Add black's move to the current trio
        if (trios.length > 0) {
          const lastTrio = trios[trios.length - 1];
          lastTrio.blackMove = move;
          lastTrio.blackIndex = moveIndex;
          lastTrio.moves.push(move);
        } else {
          // Handle case where game starts with black
          currentTrio = {
            moveNumber: moveNumber, // Use calculated move number
            whiteMove: null,
            blackMove: move,
            whiteIndex: null,
            blackIndex: moveIndex,
            moves: [move]
          };
          trios.push(currentTrio);
        }
      }
    });

    // Determine which trio is current based on the currentMove
    let trioIndex = 0;
    let currentMoveIndex = -1;

    if (currentMove !== '-1' && currentMove) {
      try {
        // IMPORTANT: For the main line display, if we're in a variation,
        // we want to highlight the main move that led to the variation
        const effectiveMove = isInVariation(currentMove) ? 
                            getMainLineMoveFromVariation(currentMove) : 
                            currentMove;
        
        // Parse the current move notation to get the index
        if (typeof effectiveMove === 'string' && effectiveMove.includes('|')) {
          const parts = effectiveMove.split('|');
          if (parts.length > 1 && parts[1]) {
            if (parts[1].includes(':')) {
              const variationParts = parts[1].split(':');
              if (variationParts.length > 1) {
                currentMoveIndex = parseInt(variationParts[variationParts.length - 1]);
                
                // Find which trio contains the current move
                trios.forEach((trio, index) => {
                  if ((trio.whiteIndex === currentMoveIndex) || (trio.blackIndex === currentMoveIndex)) {
                    trioIndex = index;
                  }
                });
              }
            } else {
              // Handle the case where there's no colon
              currentMoveIndex = parseInt(parts[1]);
              
              // Find which trio contains the current move
              trios.forEach((trio, index) => {
                if ((trio.whiteIndex === currentMoveIndex) || (trio.blackIndex === currentMoveIndex)) {
                  trioIndex = index;
                }
              });
            }
          }
        } else {
          // Handle regular move without variation notation (no pipe character)
          currentMoveIndex = parseInt(effectiveMove);
          
          // Find which trio contains the current move
          trios.forEach((trio, index) => {
            if ((trio.whiteIndex === currentMoveIndex) || (trio.blackIndex === currentMoveIndex)) {
              trioIndex = index;
            }
          });
        }
      } catch (error) {
        console.error("Error parsing current move in renderMoves:", error, currentMove);
        // Default values already set above
      }
    }

    // Calculate displayIndices - which trios to show
    let displayIndices = [];
    

    if (!isAnimating) {
      // Normal display - previous, current, next
      const prevIndex = trioIndex > 0 ? trioIndex - 1 : null;
      const nextIndex = trioIndex < trios.length - 1 ? trioIndex + 1 : null;
      displayIndices = [prevIndex, trioIndex, nextIndex].filter(i => i !== null);
    } else {
      // During animation - show 4 trios to enable the slide animation
      if (animationDirection === 'left') {
        // Sliding left - show current, next, and next+1
        const nextIndex = trioIndex < trios.length - 1 ? trioIndex + 1 : null;
        const nextNextIndex = nextIndex !== null && nextIndex < trios.length - 1 ? nextIndex + 1 : null;
        displayIndices = [trioIndex, nextIndex, nextNextIndex].filter(i => i !== null);
      } else {
        // Sliding right - show prev-1, prev, and current
        const prevIndex = trioIndex > 0 ? trioIndex - 1 : null;
        const prevPrevIndex = prevIndex !== null && prevIndex > 0 ? prevIndex - 1 : null;
        displayIndices = [prevPrevIndex, prevIndex, trioIndex].filter(i => i !== null);
      }
    }

    // Determine if we're at the beginning or end of the game
    const isAtStart = trioIndex === 0;
    const isAtEnd = trioIndex === trios.length - 1;

    // Determine the end text based on game result
    let endText = "End";
    let endTextColor = "#999";
    if (gameResultInfo && isAtEnd) {
      if (gameResultInfo.result === 'draw') {
        endText = "Draw";
        endTextColor = "tan"; // Blue for draw
      } else if (gameResultInfo.result === 'checkmate') {
        endText = "Check-\nMate";
        endTextColor = "#b71c1c"; // Red for checkmate
      }
    }

    // Render the grid with proper animation classes
    return (
      <div className="moves-container" ref={movesContainerRef}>
        {/* Previous trio or empty placeholder with "Start" indicator */}
        {trioIndex > 0 || (isAnimating && animationDirection === 'left') ? (
          <div
            className={`trio-container trio-previous ${isAnimating && animationDirection === 'right' ? 'slide-right' : ''
              }`}
          >
            {trioIndex > 0 && renderTrio(trios[trioIndex - 1], prefix, false, trioIndex === 1)}
          </div>
        ) : (
          <div
            className="trio-empty"
            onClick={onReset}
            title="Go to start position"
          >
            {isAtStart && "Start"}
          </div>
        )}

        {/* Current trio - always in center column */}
        <div
          className={`trio-container trio-current current-trio ${isAnimating ? 'trio-current-new' : ''
            }`}
        >
          {renderTrio(trios[trioIndex], prefix, true, trioIndex === 0)}
        </div>

        {/* Next trio or empty placeholder with custom end indicator */}
        {trioIndex < trios.length - 1 || (isAnimating && animationDirection === 'right') ? (
          <div
            className={`trio-container trio-next ${isAnimating && animationDirection === 'left' ? 'slide-left' : ''
              }`}
          >
            {trioIndex < trios.length - 1 && renderTrio(trios[trioIndex + 1], prefix, false, false)}
          </div>
        ) : (
          <div
            className="trio-empty-next"
            onClick={onEnd}
            title={`Go to final position (${endText.replace('\n', ' ')})`}
            style={{
              color: endTextColor,
              whiteSpace: 'pre-line',
              lineHeight: '1.2'
            }}
          >
            {isAtEnd && endText}
          </div>
        )}
      </div>
    );
  };

  // Helper function to render a single trio
  const renderTrio = (trio, prefix, isCurrent, isFirstTrio = false, variationStartingTurn = null) => {
    if (!trio) return null;

    // Determine if this is the first move and if we're starting with Black
    // Use variation-specific startingTurn if provided, otherwise use global startingTurn
    const effectiveStartingTurn = variationStartingTurn || startingTurn;
    const isStartingWithBlack = effectiveStartingTurn === 'b' && isFirstTrio;

    const shouldShowBlackFirstNotation = isStartingWithBlack && trio.blackMove && !trio.whiteMove;

    // Determine which move to navigate to when clicking the move number
    const moveNumberTargetIndex = trio.whiteIndex !== null ? trio.whiteIndex : trio.blackIndex;

    return (
      <>
        {/* Move number - clickable, navigates to first move in trio */}
        <button
          className={`move-number ${isCurrent ? 'current-number' : ''}`}
          onClick={() => handleMoveClick(`${prefix || ''}${moveNumberTargetIndex}`)}
          // style={{ color: 'red' }}
        >
          {shouldShowBlackFirstNotation ? `${trio.moveNumber}...` : `${trio.moveNumber}.`}
        </button>

        {/* White's move - only show if exists or if not starting with black */}
        {(!isStartingWithBlack && trio.whiteMove) && (
          <button
            onClick={() => handleMoveClick(`${prefix || ''}${trio.whiteIndex}`)}
            id={`${prefix || ''}${trio.whiteIndex}`}
            className={
              `move white-move` +
              `${`${prefix || ''}${trio.whiteIndex}` === currentMove ? ' current-move' : ''}` +
              `${starredMoves.includes(`${prefix || ''}${trio.whiteIndex}`) ? ' favorite-move' : ''}` +
              `${trio.whiteMove?.variations ? ' variation-move' : ''}`
            }
          >
            {trio.whiteMove.notation}
          </button>
        )}

        {/* Black's move */}
        {trio.blackMove && (
          <button
            onClick={() => handleMoveClick(`${prefix || ''}${trio.blackIndex}`)}
            id={`${prefix || ''}${trio.blackIndex}`}
            className={
              `move black-move` +
              `${`${prefix || ''}${trio.blackIndex}` === currentMove ? ' current-move' : ''}` +
              `${starredMoves.includes(`${prefix || ''}${trio.blackIndex}`) ? ' favorite-move' : ''}` +
              `${trio.blackMove?.variations ? ' variation-move' : ''}`
            }
          >
            {trio.blackMove.notation}
          </button>
        )}
      </>
    );
  };

  // Keep the variation rendering logic
  const variationJsx = (moves, prefix, numMoves) => {
    return moves?.map((move, index) => {
      const currentMoveNotation = `${prefix || ''}${index}`;
      if (move?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT) {
        <React.Fragment key={currentMoveNotation}></React.Fragment>;
      }

      return (
        <React.Fragment key={currentMoveNotation}>
          {move.variations &&
            move.variations.length !== 0 &&
            move.variations.map((variation, variationIndex) => {
              if (!variation) {
                return (
                  <React.Fragment
                    key={`${currentMoveNotation}-${variationIndex}`}
                  />
                );
              }
              if (variation.length === 0) {
                return (
                  <React.Fragment
                    key={`${currentMoveNotation}-${variationIndex}`}
                  />
                );
              }
              if (
                variation[0]?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT
              ) {
                return (
                  <React.Fragment
                    key={`${currentMoveNotation}-${variationIndex}`}
                  />
                );
              }
              if (!currentMove.startsWith(`${prefix || ''}${index}`)) {
                return (
                  <React.Fragment
                    key={`${currentMoveNotation}-${variationIndex}`}
                  />
                );
              }

              // Determine the starting color for this specific variation
              // We need to know if the parent move is white or black to determine
              // which color moves first in the variation
              const isParentMoveWhite = (index % 2 === 0) === (startingTurn === 'w');
              
              // The first move in a variation is the opposite color of the parent move
              const variationStartingTurn = isParentMoveWhite ? 'b' : 'w';

              // Check if we're actually in this variation or just viewing its parent move
              const isInThisVariation = currentMove.includes(`${prefix || ''}${index}|${variationIndex}`);
              
              // If we're just viewing the parent move but not in the variation yet,
              // always start from the first move of the variation
              const variationMovePrefix = `${prefix || ''}${index}|${variationIndex}:`;
              
              return (
                <div className="variation-container">
                  <div
                    className="variation"
                    key={`${currentMoveNotation}-${variationIndex}`}
                  >
                    Variation:
                  </div>
                  <div className="variation-with-back-btn">
                    {/* <button
                      onClick={() => handleMoveClick(`${prefix || ''}${index}`)}
                      className="back-from-variation-btn"
                    >
                      {move.notation}
                    </button> */}
                    <div className="variation-moves-container">
                      {renderVariationMoves(
                        variation,
                        variationMovePrefix,
                        Math.ceil(numMoves) + index / 2,
                        variationStartingTurn,  // Pass the variation-specific starting turn
                        isInThisVariation       // Pass whether we're actually in this variation
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </React.Fragment>
      );
    });
  };

  // Special render function for variations with 2-column layout
  const renderVariationMoves = (moves, prefix, numMoves, variationStartingTurn, isInVariation = false) => {
    if (!moves?.length) return null;

    // Group moves into trios (move number + white move + black move)
    const trios = [];
    let currentTrio = { moveNumber: null, whiteMove: null, blackMove: null, moves: [] };

    // Determine if we're starting with Black's move
    // Use the variation-specific startingTurn if provided, otherwise fall back to the global startingTurn
    const isStartingWithBlack = (variationStartingTurn || startingTurn) === 'b';

    moves.forEach((move, index) => {
      // Skip comment moves
      if (move?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT) {
        return;
      }

      const moveIndex = index;
      // Adjust the move parity based on starting turn
      const isWhiteMove = isStartingWithBlack ? 
        (moveIndex % 2 !== 0) : // If starting with black, odd indices are white moves
        (moveIndex % 2 === 0);  // If starting with white, even indices are white moves

      // Calculate appropriate move number
      let moveNumber;
      if (isStartingWithBlack) {
        moveNumber = Math.floor(moveIndex / 2) + 1;
        // For White's moves after Black started, increment the move number by 1
        if (isWhiteMove) {
          moveNumber += 1;
        }
      } else {
        moveNumber = Math.floor(moveIndex / 2) + 1;
      }

      if (isWhiteMove) {
        // Start a new trio for white's move
        currentTrio = {
          moveNumber: moveNumber,
          whiteMove: move,
          blackMove: null,
          whiteIndex: moveIndex,
          blackIndex: null,
          moves: [move]
        };
        trios.push(currentTrio);
      } else {
        // Add black's move to the current trio
        if (trios.length > 0) {
          const lastTrio = trios[trios.length - 1];
          lastTrio.blackMove = move;
          lastTrio.blackIndex = moveIndex;
          lastTrio.moves.push(move);
        } else {
          // Handle case where variation starts with black
          currentTrio = {
            moveNumber: moveNumber,
            whiteMove: null,
            blackMove: move,
            whiteIndex: null,
            blackIndex: moveIndex,
            moves: [move]
          };
          trios.push(currentTrio);
        }
      }
    });

    // Determine which trio is current based on the currentMove
    let trioIndex = 0;
    let currentMoveIndex = -1;

    if (currentMove !== '-1' && currentMove && isInVariation) {
      try {
        // Parse the current move notation to get the index
        if (typeof currentMove === 'string' && currentMove.includes('|')) {
          const parts = currentMove.split('|');
          if (parts.length > 1 && parts[1]) {
            if (parts[1].includes(':')) {
              const variationParts = parts[1].split(':');
              if (variationParts.length > 1) {
                currentMoveIndex = parseInt(variationParts[variationParts.length - 1]);
                
                // Find which trio contains the current move
                trios.forEach((trio, index) => {
                  if ((trio.whiteIndex === currentMoveIndex) || (trio.blackIndex === currentMoveIndex)) {
                    trioIndex = index;
                  }
                });
              }
            } else {
              // Handle the case where there's no colon
              currentMoveIndex = parseInt(parts[1]);
              
              // Find which trio contains the current move
              trios.forEach((trio, index) => {
                if ((trio.whiteIndex === currentMoveIndex) || (trio.blackIndex === currentMoveIndex)) {
                  trioIndex = index;
                }
              });
            }
          }
        } else {
          // Handle regular move without variation notation (no pipe character)
          currentMoveIndex = parseInt(currentMove);
          
          // Find which trio contains the current move
          trios.forEach((trio, index) => {
            if ((trio.whiteIndex === currentMoveIndex) || (trio.blackIndex === currentMoveIndex)) {
              trioIndex = index;
            }
          });
        }
      } catch (error) {
        console.error("Error parsing current move in renderMoves:", error, currentMove);
        // Default values already set above
      }
    } else {
      // If we're not in the variation, always show the first trio
      trioIndex = 0;
    }

    // For variations, only show current and next trios or placeholder
    return (
      <>
        {/* Current trio - always in left column */}
        <div className="trio-container variation-trio-current">
          {renderTrio(trios[trioIndex], prefix, true, trioIndex === 0, variationStartingTurn)}
        </div>

        {/* Next trio or placeholder */}
        {trioIndex < trios.length - 1 ? (
          <div className="trio-container variation-trio-next" style={{ marginLeft: '30px' }}>
            {renderTrio(trios[trioIndex + 1], prefix, false, false, variationStartingTurn)}
          </div>
        ) : (
          /* Show placeholder if at the end */
          // <div className="trio-placeholder-text" style={{ marginLeft: '10px' }}>...build your line...</div>
          <div> </div>
        )}
      </>
    );
  };

  // Function to handle the "Start" click - go to beginning of current variation or main line
  const handleStartClick = () => {
    const { prefix } = parseMove(currentMove);

    if (prefix) {
      // We're in a variation - navigate to the first move of this variation
      const parts = prefix.split('|');
      if (parts.length > 1) {
        // Extract the parent move and variation index
        const parentMoveIdx = parts[0];
        const variationPath = parts[1].split(':')[0]; // Remove any trailing colon

        // Navigate to the first move of this variation
        setCurrentMove(`${parentMoveIdx}|${variationPath}:0`);
      }
    } else {
      // We're in the main line - go to starting position
      onReset();
    }
  };

  // Function to handle the "End" click - go to end of current variation or main line
  const handleEndClick = () => {
    const { prefix } = parseMove(currentMove);

    // Find the last move in the current variation
    const variation = getCurrentVariation(moves, currentMove);
    if (!variation || variation.length === 0) return;

    const lastMoveIndex = variation.length - 1;
    setCurrentMove(`${prefix}${lastMoveIndex}`);
  };

  if (!moves?.length) {
    return <></>;
  }

  // Conditionally render based on type
  if (type === 'COMMENT_PREVIEW') {

    // For COMMENT_PREVIEW type, show compact version
    return renderCommentPreview(moves, userId);
  } else if (type === 'COMMENTS') {

    // For COMMENTS type, use the trio-based comment moves rendering
    return (
      <div className="comment-full-container">
        {/* Use trio rendering for comments if inside a variation, otherwise use normal rendering */}
        {currentMove.includes('|') ? (
          // We're inside a comment variation - use trio style
          <>
            {commentVariationTrioJsx(moves, undefined, userId, 0)}
          </>
        ) : (
          // We're in the main comment line - use trio style
          <div className="comment-creation-container">
            {renderCommentTrioMoves(moves, undefined, userId, 0, null)}
          </div>
        )}
      </div>
    );
  } else {
    // For default type (or any other type), use the original wrapper
    return (
      <div className="moves-variation-container">
        {renderMoves(moves, '', currentTurn === 'w' ? -0.5 : 0)}
        {variationJsx(moves, '', 0)}
      </div>
    );
  }
};

export default Moves;
